import { useRef, useEffect, Suspense, useState } from "react";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { fetchWithCache, scrollToHeaderAndFocus, setNavigate, showLog } from "./helper/utility";
import { ROUTES } from "./routes/Routes";
import { ToastContainer } from "react-toastify";
import AppRoutes from "./routes/AppRoutes";
import { SuburbModel } from "./models/SuburbModel";
import { SocialMediaLinkModel } from "./models/SocialMediaLinkModel";
import { fetchBrand, fetchCompanyInfo, fetchSocialMediaLink, fetchState, fetchSuburb, fetchVehicle } from "./services/CommonService";
import { PageModel } from "./models/PageModel";
import { fetchDynamicService } from "./services/DynamicService";
import { getCurrentLocation, LocationData } from "./helper/getCurrentLocation";
import { AppConstant } from "./constant/AppConstant";
import LocationDialog from "./layout/LocationDialog";
import { showErrorAlert } from "./helper/alertHelper";
import { getLocalStorage, setApiDataToCache, setLocalStorage } from "./helper/localStorageHelper";
import { CompanyInfoModel } from "./models/CompanyInfoModel";
import { BrandModel } from "./models/BrandModel";
import { VehicleTypeModel } from "./models/VehicleTypeModel";
import { StateModel } from "./models/StateModel";
import Header from "./layout/Header";
import Footer from "./layout/Footer";
import "./assets/css/index.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import Maintenance from "./pages/Maintenance";
import SalesIQ from "./components/SalesIQ";
import useDisableZoom from "./helper/useDisableZoom";

function App() {
  useDisableZoom();
  const location = useLocation();
  const navigate = useNavigate();
  setNavigate(navigate);

  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [suburbs, setSuburb] = useState<SuburbModel[]>([]);
  const [companyInfos, setCompanyInfos] = useState<CompanyInfoModel[]>([]);
  const [socialMediaLink, setSocialMediaLink] = useState<SocialMediaLinkModel[]>([]);
  const [dynamicServices, setDynamicServices] = useState<PageModel[]>([]);
  const [brands, setBrands] = useState<BrandModel[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeModel[]>([]);
  const [states, setStates] = useState<StateModel[]>([]);

  const isHomeRoute = location.pathname.includes(ROUTES.HOME.path);
  const isQuoteRoute = matchPath({ path: ROUTES.QUOTE.path }, location.pathname);

  const [showPopup, setShowPopup] = useState(() => {
    const storedValue = getLocalStorage(AppConstant.locationPopupShow);
    return storedValue !== null ? storedValue === "true" : true;
  });
  const fetchRef = useRef(false);

  const fetchDataFromApi = async () => {
    if (fetchRef.current) return;
    fetchRef.current = true;

    try {
      if (isHomeRoute) {
        const [{ suburbs }, { socialMediaLinks }, { dynamicServices }, { companyInfos }, { brands }, { vehicleTypes }, { states }] =
          await Promise.all([
            fetchSuburb(),
            fetchSocialMediaLink(),
            fetchDynamicService(),
            fetchCompanyInfo(),
            fetchBrand(),
            fetchVehicle(),
            fetchState(),
          ]);

        setSuburb(suburbs);
        setSocialMediaLink(socialMediaLinks);
        setDynamicServices(dynamicServices);
        setCompanyInfos(companyInfos);
        setBrands(brands);
        setVehicleTypes(vehicleTypes);
        setStates(states);

        setApiDataToCache(AppConstant.cacheSuburbs, suburbs);
        setApiDataToCache(AppConstant.cacheSocialMedia, socialMediaLinks);
        setApiDataToCache(AppConstant.cacheDynamicServices, dynamicServices);
        setApiDataToCache(AppConstant.cacheCompanyInfos, companyInfos);
        setApiDataToCache(AppConstant.cacheBrands, brands);
        setApiDataToCache(AppConstant.cacheVhicleTypes, vehicleTypes);
        setApiDataToCache(AppConstant.cacheStates, states);

      } else {
        const suburbs = await fetchWithCache(AppConstant.cacheSuburbs, async () => {
          const { suburbs } = await fetchSuburb();
          return suburbs;
        });

        const social = await fetchWithCache(AppConstant.cacheSocialMedia, async () => {
          const { socialMediaLinks } = await fetchSocialMediaLink();
          return socialMediaLinks;
        });

        const services = await fetchWithCache(AppConstant.cacheDynamicServices, async () => {
          const { dynamicServices } = await fetchDynamicService();
          return dynamicServices;
        });

        const company = await fetchWithCache(AppConstant.cacheCompanyInfos, async () => {
          const { companyInfos } = await fetchCompanyInfo();
          return companyInfos;
        });

        const brands = await fetchWithCache(AppConstant.cacheBrands, async () => {
          const { brands } = await fetchBrand();
          return brands;
        });

        const vehicleTypes = await fetchWithCache(AppConstant.cacheVhicleTypes, async () => {
          const { vehicleTypes } = await fetchVehicle();
          return vehicleTypes;
        });

        const states = await fetchWithCache(AppConstant.cacheStates, async () => {
          const { states } = await fetchState();
          return states;
        });

        setSuburb(suburbs);
        setSocialMediaLink(social);
        setDynamicServices(services);
        setCompanyInfos(company);
        setBrands(brands);
        setVehicleTypes(vehicleTypes);
        setStates(states);
      }
      const loc = await getCurrentLocation();
      setCurrentLocation(loc);
    } finally {
      fetchRef.current = false;
    }
  };

  useEffect(() => {
    if (!isQuoteRoute) {
      fetchDataFromApi();
    }
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    if (!isQuoteRoute && showPopup && currentLocation) {
      LocationDialog.show(
        currentLocation.address,
        currentLocation.postal_code,
        handleLocationConfirm,
        handleLocationChange
      );
    }
  }, [showPopup, currentLocation]);

  const handleLocationConfirm = async () => {
    if (currentLocation!!.postal_code) {
      const currentPostal = String(currentLocation!!.postal_code).toLowerCase();

      const result = suburbs.filter((suburb) => {
        return suburb.postal_code?.toLowerCase().includes(currentPostal);
      });
      if (Array.isArray(result) && result.length > 0) {
        const suburb = result[0];
        setShowPopup(false);
        setLocalStorage(AppConstant.userSelectedStateId, suburb.state_id);
        setLocalStorage(AppConstant.userSelectedCityId, suburb.city_id);
        setLocalStorage(AppConstant.userSelectedSuburbId, suburb._id);
        setLocalStorage(AppConstant.userSelectedSuburb, `${suburb.name}, ${suburb.state_name}, ${suburb.postal_code}`);
        setLocalStorage(AppConstant.userSelectedSuburbPrices, JSON.stringify(suburb.selected_city));
        setLocalStorage(AppConstant.locationPopupShow, false);

        setTimeout(() => {
          window.location.reload();
        }, 800);
      } else {
        showErrorAlert("Selected postal code is not serviceable");
      }
      // setLocalStorage(AppConstant.locationPopupShow, true);
      // setShowPopup(false);
    }
  };

  const handleLocationChange = () => {
    setShowPopup(false);

    scrollToHeaderAndFocus();
    setTimeout(() => {
      setLocalStorage(AppConstant.locationPopupShow, true);
    }, 100);
  };

  return (

    AppConstant.isMaintenance ? (
      <>
        <Maintenance />
      </>
    ) : (
      <>
        {!isQuoteRoute &&
          <Header
            suburbs={suburbs}
            socialMediaLink={socialMediaLink}
            dynamicServices={dynamicServices}
            brands={brands}
            vehicleTypes={vehicleTypes}
            states={states}
          />
        }
        <main>
          <Suspense fallback={null}>
            <AppRoutes />
            <ToastContainer />
          </Suspense>
        </main>

        {!isQuoteRoute &&
          <>
            <Footer
              companyInfos={companyInfos}
              socialMediaLink={socialMediaLink}
              dynamicServices={dynamicServices}
            />

            <SalesIQ />
          </>
        }
      </>
    )
  );
}

export default App;
